import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
} from '@mui/material';
import {
  VerifiedUser as VerifiedUserIcon,
  ShoppingCart as ShoppingCartIcon,
  Article as ArticleIcon,
  Gavel as GavelIcon,
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMutation, useQuery } from '@apollo/client';
import { REQUEST_PASSWORD_RESET } from '../graphql/mutations';
import { GET_PUBLIC_SITE_SETTINGS } from '../graphql/queries';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  // Fetch public site settings
  const { data: siteSettingsData } = useQuery(GET_PUBLIC_SITE_SETTINGS);
  const siteSettings = siteSettingsData?.publicSiteSettings;

  // Password Reset State
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const [requestPasswordReset, { loading: requestLoading }] = useMutation(REQUEST_PASSWORD_RESET, {
    onCompleted: (data) => {
      console.log('Password reset requested:', data);
      if (data.requestPasswordReset?.success) {
        setResetSuccess('Имейл с линк за възстановяване на паролата е изпратен. Моля, проверете вашата поща и кликнете на линка в имейла.');
        setResetError('');
      } else {
        setResetError(data.requestPasswordReset?.message || 'Грешка при заявката');
      }
    },
    onError: (error) => {
      console.error('Password reset request error:', error);
      setResetError('Грешка при заявката за възстановяване на паролата');
    }
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    if (!formData.username || !formData.password) {
      setError('Моля въведете потребителско име и парола');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        throw new Error('Грешно потребителско име или парола');
      }

      const data = await response.json();
      login(data);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password Reset Functions
  const resetPasswordResetState = () => {
    setResetEmail('');
    setResetError('');
    setResetSuccess('');
  };

  const handlePasswordResetOpen = () => {
    setPasswordResetOpen(true);
    resetPasswordResetState();
  };

  const handlePasswordResetClose = () => {
    setPasswordResetOpen(false);
    resetPasswordResetState();
  };

  const handleRequestPasswordReset = async () => {
    if (!resetEmail) {
      setResetError('Моля въведете имейл адрес');
      return;
    }

    console.log('Requesting password reset for:', resetEmail);
    try {
      await requestPasswordReset({
        variables: { input: { email: resetEmail } }
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(79, 172, 254, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 242, 254, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(128, 0, 255, 0.05) 0%, transparent 40%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={24}
            sx={{
              padding: 4,
              width: '100%',
              maxWidth: 420,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(15, 52, 96, 0.3)',
                }}
              >
                <ReceiptIcon sx={{ fontSize: 36, color: '#fff' }} />
              </Box>
            </Box>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: '#1a1a2e' }}>
              {siteSettings?.siteName || 'Invoicing'}
            </Typography>
            <Typography component="h2" variant="body1" align="center" gutterBottom sx={{ color: 'text.secondary', mb: 3 }}>
              Електронно фактуриране
            </Typography>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Потребителско име"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleInputChange}
              disabled={loading}
              sx={{
                '& .MuiInputBase-input': { color: '#1e293b' },
                '& .MuiInputLabel-root': { color: '#64748b' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                  '&:hover fieldset': { borderColor: '#1a1a2e' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a2e' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1a1a2e' },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Парола"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
              sx={{
                '& .MuiInputBase-input': { color: '#1e293b' },
                '& .MuiInputLabel-root': { color: '#64748b' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                  '&:hover fieldset': { borderColor: '#1a1a2e' },
                  '&.Mui-focused fieldset': { borderColor: '#1a1a2e' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1a1a2e' },
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1a1a2e 100%)',
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Вход'
              )}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handlePasswordResetOpen}
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                Забравена парола?
              </Link>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Нямате акаунт?
              </Typography>
              <Button
                component={RouterLink}
                to="/register"
                variant="outlined"
                startIcon={<PersonAddIcon />}
                fullWidth
              >
                Регистрация
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<VerifiedUserIcon />}
              href="/verify"
              sx={{ textTransform: 'none' }}
            >
              Валидатор на документи
            </Button>

            {/* External Links from Site Settings */}
            {(siteSettings?.showBlogLink || siteSettings?.showTermsLink || siteSettings?.showBuyButton) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  {siteSettings?.showBlogLink && siteSettings?.blogUrl && (
                    <Button
                      size="small"
                      startIcon={<ArticleIcon />}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      href={siteSettings.blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Помощ
                    </Button>
                  )}
                  {siteSettings?.showTermsLink && siteSettings?.termsUrl && (
                    <Button
                      size="small"
                      startIcon={<GavelIcon />}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      href={siteSettings.termsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Общи условия
                    </Button>
                  )}
                  {siteSettings?.showBuyButton && siteSettings?.shopUrl && (
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={<ShoppingCartIcon />}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      href={siteSettings.shopUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Купи
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetOpen} onClose={handlePasswordResetClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Възстановяване на парола
        </DialogTitle>
        <DialogContent>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {resetError}
            </Alert>
          )}

          {resetSuccess && (
            <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
              {resetSuccess}
            </Alert>
          )}

          {!resetSuccess && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
                Въведете вашия имейл адрес и ще ви изпратим линк за възстановяване на паролата.
              </Typography>
              <TextField
                fullWidth
                label="Имейл адрес"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                margin="normal"
                autoFocus
                sx={{
                  '& .MuiInputBase-input': { color: '#1e293b' },
                  '& .MuiInputLabel-root': { color: '#64748b' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                    '&:hover fieldset': { borderColor: '#1a1a2e' },
                    '&.Mui-focused fieldset': { borderColor: '#1a1a2e' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#1a1a2e' },
                }}
              />
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordResetClose}>
            {resetSuccess ? 'Затвори' : 'Отказ'}
          </Button>
          {!resetSuccess && (
            <Button
              onClick={handleRequestPasswordReset}
              variant="contained"
              disabled={requestLoading}
            >
              {requestLoading ? <CircularProgress size={20} /> : 'Изпрати'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
